/**
 * Hosts whose embedded curilive frame should start fresh on every load.
 * Matches the Curipod classroom simulator (curipod.com / curipod.org) and local
 * dev (localhost). Exported for unit testing without touching `window`.
 */
export function isAllowedEmbedHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === 'curipod.com' ||
    hostname.endsWith('.curipod.com') ||
    hostname === 'curipod.org' ||
    hostname.endsWith('.curipod.org')
  );
}

function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top can throw in some browsers — if it does,
    // we are definitely inside a (cross-origin) frame.
    return true;
  }
}

/**
 * True when an ancestor frame is served from an allowed Curipod host.
 * Prefers `window.location.ancestorOrigins` (Chromium/WebKit); falls back to
 * `document.referrer` (Firefox, which lacks ancestorOrigins). Checks every
 * ancestor so nested embeds still match.
 */
function embedderIsCuripod(): boolean {
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      for (let i = 0; i < ancestors.length; i++) {
        try {
          if (isAllowedEmbedHost(new URL(ancestors[i]).hostname)) return true;
        } catch {
          // Ignore unparseable origin entries.
        }
      }
      return false;
    }
  } catch {
    // ancestorOrigins unavailable — fall through to referrer.
  }

  if (document.referrer) {
    try {
      return isAllowedEmbedHost(new URL(document.referrer).hostname);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * True when curilive is loaded inside an iframe whose embedder is a Curipod host.
 */
export function shouldResetForCuripodEmbed(): boolean {
  return typeof window !== 'undefined' && inIframe() && embedderIsCuripod();
}

let didReset = false;

/**
 * One-shot fresh-start for the Curipod classroom simulator. When curilive is
 * embedded in a Curipod iframe we wipe Curipod-owned client state so a reload on
 * the same pincode starts clean instead of reusing a dead participant session
 * (which would otherwise surface the "You've been signed out" modal).
 *
 * Clears only Curipod-owned state: `@curipod-v1.*` localStorage keys, the
 * `curipod-db` IndexedDB store, and this origin's cookies. Runs at most once per
 * page load (guarded against React rerenders / StrictMode double-invoke). Must be
 * called synchronously before the join flow reads the cached participant.
 */
export function maybeResetForCuripodEmbed(): void {
  if (didReset || !shouldResetForCuripodEmbed()) return;
  didReset = true;

  // Lazy-require so importing the pure host predicate (e.g. in tests, or before
  // `window`/`indexedDB` exist) doesn't pull in the storage singletons.
  const LocalstorageService = require('@/LocalstorageService').default;
  const IndexedDBStorageService = require('@/IndexedDBStorageService').default;
  const { clearAllCookies } = require('@/utils/cookies');

  LocalstorageService.clearAllCuripod();
  try {
    // NOTE: this also clears the NextAuth student-SSO session cookie, so an
    // embedded frame starts with no IdP identity. That is consistent rather than
    // harmful: the OAuth redirect cannot complete inside an iframe anyway, so
    // StudentAuthGate detects the frame and offers "open in a new tab" instead.
    clearAllCookies();
  } catch {
    // Best-effort.
  }
  // File cache is not auth-related — clear it without blocking the join flow.
  void IndexedDBStorageService.clearAll();
}
